import { Mentions } from 'antd'
import { useRef, useState, useEffect } from 'react'
import EmojiPicker from 'emoji-picker-react'
import { SmileOutlined } from '@ant-design/icons'
import './MentionEmojiInputDark.css'

const { Option } = Mentions

const MentionEmojiInput = ({
  value,
  onChange,
  fetchMentions,
  placeholder = 'Write something...',
  onMentionMapChange,
}) => {
  const mentionRef = useRef(null)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [mentionOptions, setMentionOptions] = useState([])
  const mentionMapRef = useRef({})
  const pickerRef = useRef(null)
  const [cursorPosition, setCursorPosition] = useState({ start: 0, end: 0 })

  const handleEmojiSelect = (emojiObject) => {
    const textarea = document.getElementById(
      'editTextArea'
    ) as HTMLTextAreaElement
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    onChange((prevContent) => {
      const textBefore = prevContent.slice(0, start)
      const textAfter = prevContent.slice(end)
      return textBefore + emojiObject.emoji + textAfter
    })
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd =
        start + emojiObject.emoji.length
      textarea.focus()
    }, 0)
  }

  const handleEmojiButtonClick = () => {
    const input = mentionRef.current?.resizableTextArea?.textArea
    if (input) {
      setCursorPosition({
        start: input.selectionStart,
        end: input.selectionEnd,
      })
    }
    setShowEmojiPicker(!showEmojiPicker)
  }

  const handleSearch = async (text, prefix) => {
    if (prefix === '@') {
      const users = await fetchMentions(text)
      const newMap = { ...mentionMapRef.current }

      users.forEach((u) => {
        newMap[u.label] = u.rawId
      })
      mentionMapRef.current = newMap
      setMentionOptions(users)
      onMentionMapChange?.((prev) => ({
        ...prev,
        ...mentionMapRef.current,
      }))
    }
  }

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        showEmojiPicker &&
        !pickerRef.current?.contains(e.target) &&
        !e.target.closest('.anticon-smile')
      ) {
        setShowEmojiPicker(false)
      }
    }

    const handleKeyPress = (e) => {
      if (e.key === 'Escape' && showEmojiPicker) {
        setShowEmojiPicker(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyPress)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyPress)
    }
  }, [showEmojiPicker])

  useEffect(() => {
    const input = mentionRef.current?.resizableTextArea?.textArea
    if (!input || !showEmojiPicker) return

    const updatePosition = () => {
      setCursorPosition({
        start: input.selectionStart,
        end: input.selectionEnd,
      })
    }

    input.addEventListener('click', updatePosition)
    input.addEventListener('keyup', updatePosition)
    input.addEventListener('select', updatePosition)

    return () => {
      input.removeEventListener('click', updatePosition)
      input.removeEventListener('keyup', updatePosition)
      input.removeEventListener('select', updatePosition)
    }
  }, [showEmojiPicker])

  return (
    <div className="mention-emoji-wrapper" style={{ position: 'relative' }}>
      <Mentions
        id="editTextArea"
        ref={mentionRef}
        value={value}
        onChange={onChange}
        onSearch={handleSearch}
        placeholder={placeholder}
        prefix={['@']}
        split=" "
        autoSize={{ minRows: 4, maxRows: 6 }}
        dropdownClassName="mention-dropdown-dark"
      >
        {mentionOptions.map((user) => (
          <Option key={user.value} value={user.value}>
            {user.label}
          </Option>
        ))}
      </Mentions>

      <SmileOutlined
        onClick={handleEmojiButtonClick}
        style={{
          position: 'absolute',
          top: -24,
          right: 0,
          fontSize: 20,
          cursor: 'pointer',
          color: '#ffbf00',
        }}
      />

      {showEmojiPicker && (
        <div
          ref={pickerRef}
          className="emoji-picker-container"
          style={{
            position: 'absolute',
            top: 40,
            right: 0,
            zIndex: 1000,
          }}
        >
          <EmojiPicker
            onEmojiClick={handleEmojiSelect}
            lazyLoadEmojis={true}
            previewConfig={{ showPreview: false }}
          />
        </div>
      )}
    </div>
  )
}

export default MentionEmojiInput
